import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Mail, CheckCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function ResetPassword() {
  const { resetPassword } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [email, setEmail] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    // TRATAMENTO: Limpa espaços e padroniza para minúsculo
    const cleanEmail = email.trim().toLowerCase();
    
    const result = await resetPassword(cleanEmail);
    
    setIsLoading(false);
    
    if (result.success) {
      setIsEmailSent(true);
      toast.success("E-mail enviado! Vai lá conferir (até na caixa de spam, vai que...)");
    } else {
      toast.error(result.error);
    }
  };

  if (isEmailSent) {
    return (
      <div className="min-h-screen bg-background px-6 py-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex flex-col items-center justify-center min-h-[80vh] text-center"
        >
          <div className="w-20 h-20 rounded-full bg-success/10 flex items-center justify-center mb-6">
            <CheckCircle className="w-10 h-10 text-success" />
          </div>
          <h1 className="text-2xl font-extrabold tracking-tight text-foreground mb-2">
            E-mail enviado!
          </h1>
          <p className="text-muted-foreground max-w-xs mb-8">
            Verifique sua caixa de entrada e siga as instruções para redefinir sua senha.
          </p>
          <Link to="/login">
            <Button className="h-14 px-8 rounded-full font-bold btn-primary">
              Voltar para login
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background px-6 py-8">
      {/* Back Button */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
      >
        <Link to="/login" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="w-5 h-5" />
          <span className="font-medium">Voltar</span>
        </Link>
      </motion.div>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mt-8 mb-8"
      >
        <h1 className="text-3xl font-extrabold tracking-tight text-foreground mb-2">
          Esqueceu a senha?
        </h1>
        <p className="text-muted-foreground">
          Sem problemas! Digite seu e-mail e enviaremos um link para redefinir.
        </p>
      </motion.div>

      {/* Form */}
      <motion.form
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        onSubmit={handleSubmit}
        className="space-y-6"
      >
        {/* Email */}
        <div className="space-y-2">
          <Label htmlFor="email" className="font-medium">
            E-mail cadastrado
          </Label>
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              id="email"
              type="email"
              placeholder="seu@email.com"
              data-testid="reset-email"
              className="input-modern pl-12"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
        </div>

        {/* Submit */}
        <Button
          type="submit"
          className="w-full h-14 rounded-full font-bold text-lg btn-primary"
          disabled={isLoading}
          data-testid="reset-submit"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Enviando link...
            </>
          ) : (
            "Enviar link de recuperação"
          )}
        </Button>
      </motion.form>
    </div>
  );
}
